import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram } from '@if/core/types'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { PageError, PageSpinner } from '@src/components'
import { useAppSelector } from '@src/data/store'
import { appSelectors } from '@src/data/store/AppStore'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication'
import { mechanicalWarrantyWorksheetSelectors } from '@src/data/store/MechanicalWarrantyWorksheet'
import mechanicalWarrantyWorksheetEffects from '@src/data/store/MechanicalWarrantyWorksheet/mechanical-warranty-worksheet-effects'
import { reportErrorToConsole } from '@src/services/error-logger'
import { isValidFirstPayment } from '@src/services/paymentDate-utils'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MechanicalWarrantyComputeRequestDto } from '../types/ComputeMechanicalWarrantyRequestDto'
import {
  buildMechanicalWarrantyWorksheetSchema,
  MechanicalWarrantyWorksheetDto,
} from '../types/mechanicalWarrantyWorksheetSchema'
import MechanicalWarrantyExtended from './components/MechanicalWarrantyExtended'
import MechanicalWarrantyWorksheetDetails from './components/MechanicalWarrantyWorksheetDetails'
import MechanicalWarrantyWorksheetLoanPaymentDetails from './components/MechanicalWarrantyWorksheetLoanPaymentDetails'

import { useFirstPaymentDateOptions } from '../../editWorksheet-hooks'

const EditMechanicalWarrantyWorksheetPage = () => {
  const worksheet = useAppSelector(mechanicalWarrantyWorksheetSelectors.getCurrent)!
  const creditApplication = useAppSelector(creditSelectors.getCurrent)!
  const defaultValues = worksheet ?? buildMechanicalWarrantyWorksheetSchema(creditApplication)

  const { setValue, getValues, watch, handleSubmit, control } = useForm<MechanicalWarrantyWorksheetDto>({
    mode: 'onBlur',
    defaultValues: defaultValues as MechanicalWarrantyWorksheetDto,
    resolver: yupResolver(buildMechanicalWarrantyWorksheetSchema(creditApplication)),
  })

  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const { t } = useTranslation()

  const [
    paymentFrequency,
    term,
    firstPaymentOn,
    deliveryOn,
    options,
    surcharges,
    providerId,
    mechanicalWarrantyProductId,
  ] = watch([
    'paymentFrequency',
    'term',
    'firstPaymentOn',
    'deliveryOn',
    'additions.options',
    'additions.surcharges',
    'providerId',
    'mechanicalWarrantyProductId',
  ])
  const isComputingMechanicalWarrantyLoan = useAppSelector(
    mechanicalWarrantyWorksheetSelectors.isComputingMechanicalWarrantyLoan,
  )
  const computedInfo = useAppSelector(mechanicalWarrantyWorksheetSelectors.getComputedInfo)
  const isLoadingCreditApplication = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const error = useAppSelector(appSelectors.getBusinessError)
  const mechanicalWarrantyProviders = useAppSelector(mechanicalWarrantyWorksheetSelectors.getProviders)
  const mechanicalProducts = useAppSelector(mechanicalWarrantyWorksheetSelectors.getProductsByProvider)
  const isLoadingProviders = useAppSelector(mechanicalWarrantyWorksheetSelectors.isLoadingProviders)

  // Create a ref to store the debounced function
  const debouncedFunctionRef = useRef<ReturnType<typeof debounce> | null>(null)

  useEffect(() => {
    if (creditApplication?.merchantId) {
      dispatchEffect(
        mechanicalWarrantyWorksheetEffects.getMechanicalWarrantyProviders(creditApplication.merchantId),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.merchantId, dispatchEffect])

  useEffect(() => {
    if (providerId) {
      dispatchEffect(
        mechanicalWarrantyWorksheetEffects.getMechanicalWarrantyProviderProducts(
          creditApplication?.merchantId,
          providerId,
        ),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.merchantId, dispatchEffect, providerId])

  const computeFunding = useCallback(
    (computeDto: MechanicalWarrantyComputeRequestDto) => {
      return dispatchEffect(mechanicalWarrantyWorksheetEffects.computeMechanicalWarrantyLoan(computeDto))
    },
    [dispatchEffect],
  )

  // Create debounced function only once
  useEffect(() => {
    debouncedFunctionRef.current = debounce(computeFunding, 500)
    return () => {
      debouncedFunctionRef.current?.cancel()
    }
  }, [computeFunding])

  useEffect(() => {
    if (
      [paymentFrequency, term, deliveryOn, firstPaymentOn].every((x) => !!x) &&
      isValidFirstPayment(deliveryOn, firstPaymentOn)
    ) {
      const calculationData = {
        creditApplicationId: creditApplication.id,
        providerId: providerId,
        paymentFrequency: paymentFrequency,
        term,
        firstPaymentDate: firstPaymentOn,
        deliveryOn: deliveryOn,
        options: options,
        surcharges: surcharges,
        mechanicalWarrantyProductId: mechanicalWarrantyProductId,
      } as MechanicalWarrantyComputeRequestDto

      if (debouncedFunctionRef.current) {
        try {
          debouncedFunctionRef.current(calculationData)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    }
  }, [
    paymentFrequency,
    term,
    firstPaymentOn,
    deliveryOn,
    options,
    surcharges,
    creditApplication?.id,
    providerId,
    mechanicalWarrantyProductId,
  ])

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: false,
    setValue,
    getValues,
  })

  const onSubmit = async (data: MechanicalWarrantyWorksheetDto) => {
    await dispatchEffect(mechanicalWarrantyWorksheetEffects.update(data)).then(() =>
      dispatchEffect(
        fullCreditApplicationEffects.getById(creditApplication.id, EFinancingProgram.MechanicalWarranty),
      ).then(() =>
        navigate(`/Applications/${EFinancingProgram.MechanicalWarranty}/${creditApplication.id}/view`, {
          replace: true,
        }),
      ),
    )
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isLoadingCreditApplication || isLoadingProviders} />
      <Stack>
        <PageError errors={error} />
      </Stack>
      {creditApplication && mechanicalWarrantyProviders.length > 0 && (
        <form onSubmit={handleSubmit(onSubmit, reportErrorToConsole)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack direction="row" spacing={2}>
                <Typography flexGrow={1} variant="h4">
                  {t('mechanicalWarrantyWorksheet.title')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth={false} variant="contained" onClick={() => navigate(-1)}>
                    {t('common.previous')}
                  </Button>
                  <Button
                    fullWidth={false}
                    variant="contained"
                    type="submit"
                    disabled={isComputingMechanicalWarrantyLoan || !computedInfo}
                  >
                    {t('common.save')}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={4}>
                <MechanicalWarrantyExtended
                  merchantMechanicalWarrantyProvider={mechanicalWarrantyProviders[0]} //There is only one provider for mechanical warranty as of now
                  control={control}
                  setValue={setValue}
                  mechanicalWarrantyProducts={mechanicalProducts}
                />
              </Grid>
              <Grid size={8}>
                <Stack direction="column" spacing={2} sx={{ height: '100%' }}>
                  <MechanicalWarrantyWorksheetDetails
                    creditApplication={creditApplication}
                    computedLoanAmounts={computedInfo}
                    isComputing={isComputingMechanicalWarrantyLoan}
                    control={control}
                    setValue={setValue}
                    mechanicalWarrantyProducts={mechanicalProducts}
                    minFirstPaymentDate={minFirstPaymentDate}
                    maxFirstPaymentDate={maxFirstPaymentDate}
                  />
                  <MechanicalWarrantyWorksheetLoanPaymentDetails
                    creditApplication={creditApplication}
                    computedLoanAmounts={computedInfo}
                    isComputing={isComputingMechanicalWarrantyLoan}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </div>
  )
}

export default EditMechanicalWarrantyWorksheetPage
